import React from 'react';
import { Check, ArrowRight, Share2, Copy, Info } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент для отображения результата конвертации
 */
const ConversionResult = ({ result, unitDefinitions }) => {
  if (!result) return null;
  
  const { fromValue, fromUnit, toValue, toUnit, formula } = result;
  
  // Получаем детальную информацию о единицах
  const fromUnitInfo = unitDefinitions[fromUnit];
  const toUnitInfo = unitDefinitions[toUnit];
  
  // Форматирование для отображения
  const formattedFromValue = formatNumber(fromValue, 8);
  const formattedToValue = formatNumber(toValue, 8);
  
  // Копирование результата в буфер обмена
  const copyToClipboard = () => {
    const textToCopy = `${fromValue} ${fromUnitInfo?.shortName || fromUnit} = ${toValue} ${toUnitInfo?.shortName || toUnit}`;
    navigator.clipboard.writeText(textToCopy)
      .then(() => {
        alert('Результат скопирован в буфер обмена');
      })
      .catch(err => {
        console.error('Ошибка копирования: ', err);
      });
  };
  
  // Поделиться результатом
  const shareResult = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Результат конвертации расстояний',
        text: `${fromValue} ${fromUnitInfo?.shortName || fromUnit} = ${toValue} ${toUnitInfo?.shortName || toUnit}`
      })
      .catch(err => {
        console.error('Ошибка при попытке поделиться: ', err);
      });
    } else {
      alert('Функция "Поделиться" не поддерживается вашим браузером. Вы можете скопировать результат и поделиться им вручную.');
    }
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-8 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <Check className="w-5 h-5 text-green-500" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          Результат конвертации
        </h2>
      </div>
      
      {/* Основной результат - крупно */}
      <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 py-5 px-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-600 mb-6">
        <div className="text-center md:text-right">
          <div className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            {formattedFromValue}
          </div>
          <div className="text-lg text-gray-700 dark:text-gray-300">
            {fromUnitInfo?.namePlural || fromUnit}
          </div>
        </div>
        
        <ArrowRight className="hidden md:block w-6 h-6 text-gray-400 dark:text-gray-500 flex-shrink-0" />
        <div className="md:hidden w-16 h-px bg-gray-300 dark:bg-gray-600"></div>
        
        <div className="text-center md:text-left">
          <div className="text-2xl sm:text-3xl font-bold text-purple-600 dark:text-purple-400">
            {formattedToValue}
          </div>
          <div className="text-lg text-gray-700 dark:text-gray-300">
            {toUnitInfo?.namePlural || toUnit}
          </div>
        </div>
      </div>
      
      {/* Формула и дополнительная информация */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Левая колонка - Формула */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Формула для перевода
          </h3>
          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 text-sm">
            <code className="text-purple-600 dark:text-purple-400 font-mono">
              {formula}
            </code>
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              1 {fromUnitInfo?.name} = {fromUnitInfo?.baseConversion / toUnitInfo?.baseConversion} {toUnitInfo?.namePlural}
            </div>
          </div>
        </div>
        
        {/* Правая колонка - Информация о единицах */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Справка о единицах измерения
          </h3>
          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 text-sm">
            <p className="mb-1">
              <span className="font-medium text-gray-800 dark:text-gray-200">{fromUnitInfo?.name}:</span> {fromUnitInfo?.description || 'Единица измерения расстояния'}
            </p>
            <p>
              <span className="font-medium text-gray-800 dark:text-gray-200">{toUnitInfo?.name}:</span> {toUnitInfo?.description || 'Единица измерения расстояния'}
            </p>
          </div>
        </div>
      </div>
      
      {/* Кнопки действий */}
      <div className="flex gap-3 mt-6">
        <Button
          size="sm"
          variant="outline"
          icon={<Copy className="w-4 h-4" />}
          onClick={copyToClipboard}
        >
          Копировать
        </Button>
        
        <Button
          size="sm"
          variant="outline"
          icon={<Share2 className="w-4 h-4" />}
          onClick={shareResult}
        >
          Поделиться
        </Button>
      </div>
      
      {/* Полезный совет о конвертации */}
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
        <div className="flex gap-3">
          <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
              Знаете ли вы?
            </div>
            <p className="text-sm text-blue-600 dark:text-blue-400">
              {fromUnitInfo?.system === 'russian' || toUnitInfo?.system === 'russian' ? 
                'Русская система мер, включавшая версты, сажени, аршины и вершки, использовалась до введения метрической системы в России в 1924 году. В литературе XIX века эти единицы встречаются повсеместно.' :
                'Метрическая система мер, основанная на метре, используется практически во всех странах мира кроме США, которые по-прежнему применяют имперскую систему в повседневной жизни.'
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversionResult;